
import { Injectable, OnDestroy } from '@angular/core';

import { Observable, Subscription, BehaviorSubject, combineLatest } from 'rxjs';
import { filter, map, shareReplay, switchMap, tap } from 'rxjs/operators';

import { TargetingPreferencesApi } from '../../../api/targeting/targeting-preferences.api';
import { TargetingQuotaApi } from '../../../api/targeting/targeting-quota.api';
import { IncludeExcludeModel } from '../../Misc/models/include-exclude-model';
import { JournodexGeographyModel } from '../../Journodex/models/journodex-geography.model';
import { JournodexRegionsService } from '../../Journodex/services/journodex-regions.service';
import { DistributionQuotaInfo } from '../../Charts/models/distribution.quota.info';
import { TargetingQuotaInfo } from '../model/targeting.quota.info';
import { CurrentUserStateService } from '../../../Core/Users/services/current-user-state.service';


@Injectable()
export class TargetingPreferencesService implements OnDestroy {

    private subscriptions: Subscription[] = [];
    private refreshAction$ = new BehaviorSubject<void>(null);

    public readonly targetingPreferences$ = this.refreshAction$.pipe(
        switchMap(() => this.targetingPreferencesApi.getOwnAccountPreferences()),
        shareReplay(1)
    );

    public readonly distributionQuotaInfo$ = this.targetingPreferences$.pipe(
        map(prefs => new DistributionQuotaInfo(prefs))
    );

    public readonly quotaPrefs$ = this.refreshAction$.pipe(
        switchMap(() => this.targetingQuotaApi.getCurrentAccountTargetingQuota()),
        map(quota => new TargetingQuotaInfo(quota)),
        shareReplay(1)
    );

    public readonly accountGeoPreferences$ = this.targetingPreferences$.pipe(
        switchMap(prefs => {
            const ids = [...prefs.geography.include, ...prefs.geography.exclude];
            return this.journodexRegionsService.getByGeographyIds(ids).pipe(
                map(geographies => {
                    const accountGeoPreferences = new IncludeExcludeModel<JournodexGeographyModel>();
                    for (const geoId of prefs.geography.include) {
                        accountGeoPreferences.include.push(geographies.find(x => x.collectionId === geoId));
                    }
                    for (const geoId of prefs.geography.exclude) {
                        accountGeoPreferences.exclude.push(geographies.find(x => x.collectionId === geoId));
                    }
                    return accountGeoPreferences;
                })
            );
        }),
    );

    public readonly mentionLinksEnabled$ = combineLatest([
        this.currentUserStateService.hasAdvancedMonitoringAccess(),
        this.targetingPreferences$.pipe(
            filter(x => !!x),
            map(x => x.mentionLinksEnabled)
        )
    ]).pipe(
        map(([hasMonitoring, mentionLinksEnabled]) => {
            return hasMonitoring || mentionLinksEnabled;
        })
    );

    constructor(
        private targetingPreferencesApi: TargetingPreferencesApi,
        private targetingQuotaApi: TargetingQuotaApi,
        private journodexRegionsService: JournodexRegionsService,
        private currentUserStateService: CurrentUserStateService
    ) {
        this.subscriptions.push(
            this.targetingPreferences$.subscribe(),
            this.quotaPrefs$.subscribe()
        );
    }

    ngOnDestroy() {
        this.subscriptions.forEach(
            subscription => subscription.unsubscribe()
        );
    }

    reloadPreferences() {
        this.refreshAction$.next();
    }

    buyQuota(amount: number): Observable<void> {
        return this.targetingPreferencesApi.purchaseRecipients(amount).pipe(
            tap(done => this.reloadPreferences())
        );
    }

    /**
     * Retrieves the reset date of the user's current account via accounts/mine
     */
    getQuotaExpiryDate(): Observable<Date> {
        return this.quotaPrefs$.pipe(
            map(quota => quota.customResetDate || quota.nextResetDate)
        );
    }

    /**
     * Retrieves the start date of the user's current quota period via accounts/mine
     */
    getQuotaStartDate(): Observable<Date> {
        return this.quotaPrefs$.pipe(
            map(quota => quota.periodStartDate)
        );
    }

    /**
     * Retrieves the targeting quota reset date of the given account id
     */
    getQuotaExpiryDateByAccountId(accountId: number): Observable<Date> {
        return this.targetingQuotaApi.getAccountTargetingQuota(accountId).pipe(
            map(quota => new TargetingQuotaInfo(quota)),
            map(quota => quota.nextResetDate)
        );
    }

    /**
     * Retrieves the targeting quota reset date of the given account id
     */
    getQuotaInfoByAccountId(accountId: number): Observable<TargetingQuotaInfo> {
        return this.targetingQuotaApi.getAccountTargetingQuota(accountId).pipe(
            map(quota => new TargetingQuotaInfo(quota))
        );
    }


    setAccountQuotaResetDate(accountId: number, date: Date): Observable<void> {
        return this.targetingQuotaApi.setAccountQuotaResetDate(accountId, date);
    }
}
